import { Label } from "@tellescope/design-system";
import { NumberInput } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { TellescopeFormFieldBaseProps } from "../types";
import { FieldDescription } from "../FieldDescription";
import { useTranslation } from "react-i18next";
import type { RepeatInputProps } from "../hooks/use-tellescope-enduser-form";
import { NUMBER_RESPONSE_LIMIT } from "../hooks/validate-field-by-type";

export interface NumberFieldProps extends TellescopeFormFieldBaseProps<"number"> {
  repeat?: RepeatInputProps;
}

export const NumberField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  options,
  error,
  onBlur,
  touched,
  disabled,
  required,
  repeat,
  autoFocus,
}: NumberFieldProps) => {
  const { t } = useTranslation("forms");

  const resolvedPlaceholder = placeholder ?? t("Enter a number...");
  const requireRepeat = !!options?.repeat;
  const showError = !!(error && touched);
  const showRepeatMismatch = !!repeat && repeat.repeatTouched && repeat.repeatMismatch;

  return (
    <div className="space-y-1">
      <Label required={required}>{title || resolvedPlaceholder}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <NumberInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={resolvedPlaceholder}
        min={-NUMBER_RESPONSE_LIMIT}
        max={NUMBER_RESPONSE_LIMIT}
        className={cn(className, showError && "border-destructive")}
        aria-invalid={showError}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
      {requireRepeat && !!repeat && (
        <div className="space-y-1 pt-2">
          <Label required={required}>{t("Confirm {{title}}", { title: title ?? "" }).trim()}</Label>
          <NumberInput
            value={repeat.repeatValue === "" ? undefined : Number(repeat.repeatValue)}
            onChange={(newValue) => repeat.onRepeatChange(newValue === undefined ? "" : String(newValue))}
            onBlur={repeat.onRepeatBlur}
            placeholder={resolvedPlaceholder}
            min={-NUMBER_RESPONSE_LIMIT}
            max={NUMBER_RESPONSE_LIMIT}
            className={cn(className, showRepeatMismatch && "border-destructive")}
            aria-invalid={showRepeatMismatch}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};
